import { FC } from "react";
import { IconBook2, IconFile, IconFileTypePdf } from "@tabler/icons-react";
import { Stack } from "@altimateai/lego";
import { MessageAttachment } from "../../types";

interface AttachmentListProps {
  attachments: MessageAttachment[];
}

const getAttachmentIcon = (attachment: MessageAttachment) => {
  if (attachment.attachment_type === "knowledge_base") {
    return <IconBook2 className="al-h-6 al-w-6 al-text-brand" />;
  }

  if (attachment.attachment_type === "file") {
    const fileType = attachment.file_type?.toLowerCase() || "";
    const fileName = attachment.attachment_name?.toLowerCase() || "";

    // Check both MIME type and file extension for PDF
    if (fileType === "application/pdf" || fileName.endsWith(".pdf")) {
      return <IconFileTypePdf className="al-h-6 al-w-6 al-text-brand" />;
    }

    return <IconFile className="al-h-6 al-w-6 al-text-brand" />;
  }

  // Default for other types
  return <IconFile className="al-h-6 al-w-6 al-text-brand" />;
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "";

  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const AttachmentList: FC<AttachmentListProps> = ({ attachments }) => {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  // Sort attachments by order
  const sortedAttachments = [...attachments].sort((a, b) => a.order - b.order);

  return (
    <Stack
      direction="column"
      className="al-gap-2 al-mb-3 al-overflow-x-auto al-flex-nowrap al-pb-2 al-max-w-[20rem] al-min-w-[20rem]"
      role="list"
      aria-label="Message attachments"
    >
      {sortedAttachments.map((attachment) => (
        <Stack
          key={attachment.id}
          direction="row"
          align="center"
          className="al-bg-blue-50 al-border al-border-blue-500 al-px-3 al-py-2 al-rounded-md al-gap-2 al-flex-shrink-0 al-min-w-0"
          role="listitem"
        >
          <div className="al-min-w-6 al-min-h-6">
            {getAttachmentIcon(attachment)}
          </div>
          <Stack direction="column" className="al-min-w-0">
            <span
              className="al-text-sm al-font-medium al-truncate"
              title={attachment.attachment_name}
            >
              {attachment.attachment_name}
            </span>
            {attachment.file_size && (
              <span className="al-text-xs al-text-gray-500">
                {formatFileSize(attachment.file_size)}
              </span>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
